import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2, AlertTriangle } from 'lucide-react'
import UiButton from '../ui/UiButton'

/**
 * Danger zone section for deleting the user account.
 *
 * Props:
 * - deleting: boolean
 * - onDelete: () => void | Promise
 */
export default function DeleteAccountSection({ deleting, onDelete }) {
  const { t } = useTranslation()
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const handleCancel = () => {
    setShowConfirm(false)
    setConfirmed(false)
  }

  const handleDelete = async () => {
    if (!confirmed) return
    await onDelete()
    // In a real app, the backend would log the user out and redirect.
    window.location.href = '/'
  }

  return (
    <div className="pt-8 border-t border-brand-blue/20">
      {!showConfirm ? (
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          className="inline-flex items-center gap-2 text-red-600 font-bold hover:underline"
        >
          <Trash2 size={18} />
          {t('library.profile.deleteAccount')}
        </button>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={22} />
            <div>
              <h4 className="font-bold text-red-800 mb-1">
                {t('library.profile.deleteAccount')}
              </h4>
              <p className="text-sm text-red-800">
                {t('library.profile.deleteWarning')}
              </p>
            </div>
          </div>

          <label className="flex items-start gap-3 text-red-800 text-sm mb-5 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>{t('library.profile.deleteConfirm')}</span>
          </label>

          <div className="flex flex-wrap gap-3">
            <UiButton
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={deleting}
            >
              {t('common.cancel')}
            </UiButton>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!confirmed || deleting}
              className="inline-flex items-center gap-2 bg-red-600 text-white rounded-full px-6 py-2.5 font-bold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={18} />
              {deleting ? t('common.loading') : t('library.profile.deleteAccount')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
